import { useEffect } from 'react';
import type {
	attachClosestEdge,
	extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import type {
	attachInstruction,
	extractInstruction,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item';
import type { getReorderDestinationIndex } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index';
import type { autoScroller } from '@atlaskit/pragmatic-drag-and-drop-react-beautiful-dnd-autoscroll';
import type { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import type {
	draggable,
	monitorForElements,
	dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { importForInteraction, waitForAllResources } from '@atlassian/react-async';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import { createStore, createHook } from '@atlassian/react-sweet-state';

export const getElementAdapter = () =>
	importForInteraction(
		() =>
			import(
				/* webpackChunkName: "async-drag-and-drop-element" */ '@atlaskit/pragmatic-drag-and-drop/element/adapter'
			),
	);

export const getAutoScrollPlugin = () =>
	importForInteraction(
		() =>
			import(
				/* webpackChunkName: "async-drag-and-drop-react-beautiful-dnd-autoscroll" */ '@atlaskit/pragmatic-drag-and-drop-react-beautiful-dnd-autoscroll'
			),
	);

export const getClosestEdgeAddon = () =>
	importForInteraction(
		() =>
			import(
				/* webpackChunkName: "async-drag-and-drop-closest-edge-addon" */ '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge'
			),
	);

export const getReorderDestinationIndexAddon = () =>
	importForInteraction(
		() =>
			import(
				/* webpackChunkName: "async-drag-and-drop-get-reorder-destination-index-addon" */ '@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index'
			),
	);

export const getCombineUtil = () =>
	importForInteraction(
		() =>
			import(
				/* webpackChunkName: "async-drag-and-drop-combine" */ '@atlaskit/pragmatic-drag-and-drop/combine'
			),
	);

export const getTreeItemInstruction = () =>
	importForInteraction(
		() =>
			import(
				/* webpackChunkName: "async-drag-and-drop-tree-item" */ '@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item'
			),
	);

export const STORAGE_KEY = 'column.widths';

type State = {
	draggable: typeof draggable;
	monitorForElements: typeof monitorForElements;
	dropTargetForElements: typeof dropTargetForElements;
	autoScroller: typeof autoScroller;
	attachClosestEdge: typeof attachClosestEdge;
	extractClosestEdge: typeof extractClosestEdge;
	getReorderDestinationIndex: typeof getReorderDestinationIndex;
	combine: typeof combine;
	attachInstruction: typeof attachInstruction;
	extractInstruction: typeof extractInstruction;
};

const actions = {
	loadModules:
		() =>
		({ setState }: { setState: (arg1: State) => void }) => {
			waitForAllResources([
				getElementAdapter(),
				getAutoScrollPlugin(),
				getClosestEdgeAddon(),
				getReorderDestinationIndexAddon(),
				getCombineUtil(),
				getTreeItemInstruction(),
			]).onComplete(
				([
					{ draggable, monitorForElements, dropTargetForElements },
					{ autoScroller },
					{ attachClosestEdge, extractClosestEdge },
					// @ts-expect-error waitForAllResources has a wrong type
					{ getReorderDestinationIndex },
					// @ts-expect-error same as above
					{ combine },
					// @ts-expect-error same as above
					{ attachInstruction, extractInstruction },
				]) => {
					setState({
						draggable,
						monitorForElements,
						dropTargetForElements,
						autoScroller,
						attachClosestEdge,
						extractClosestEdge,
						getReorderDestinationIndex,
						combine,
						attachInstruction,
						extractInstruction,
					});
				},
			);
		},
} as const;

type Actions = typeof actions;

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-empty-function
const noop: any = () => () => {};

const Store = createStore<State, Actions>({
	initialState: {
		draggable: noop,
		monitorForElements: noop,
		dropTargetForElements: noop,
		autoScroller: { start: noop, stop: noop, updateInput: noop },
		attachClosestEdge: noop,
		extractClosestEdge: noop,
		getReorderDestinationIndex: noop,
		combine: noop,
		attachInstruction: noop,
		extractInstruction: noop,
	},
	actions,
	name: 'JWMDragAndDropAsyncModulesStore',
});

const useHook = createHook(Store);

export const useDragAndDropAsyncModules = () => {
	const [asyncModules, { loadModules }] = useHook();

	useEffect(() => {
		loadModules();
	}, [loadModules]);

	return asyncModules;
};
